import "./ReviewStoryboard.css";
import { Alert, Button, Card, Col, Input, Row, Spin } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useMemo, useState } from "react";
import { fetchRequest } from "../../../../utils/Fetcher";

const ReviewStoryboard = ({ scenes = [], setScenes, loading, loadingDesc, error, storyboardId }) => {
  const [modes, setModes] = useState({});           
  const [editedById, setEditedById] = useState({}); 
  const [reviseTextById, setReviseTextById] = useState({}); 

  const [revisingKey, setRevisingKey] = useState(null);
  const [editingKey, setEditingKey] = useState(null);

  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const promptIdOf = (scene) =>
    String(
      scene?.id ??
      ""
    );

  const setMode = (promptId, mode) => setModes((p) => ({ ...p, [promptId]: mode }));

  const doneMode = (promptId) => {
    setModes((prev) => {
      const next = { ...prev };
      delete next[promptId];
      return next;
    });
    setEditedById((prev) => {
      const next = { ...prev };
      delete next[promptId];
      return next;
    });
    setReviseTextById((prev) => {
      const next = { ...prev };
      delete next[promptId];
      return next;
    });
  };

  const apiRevisePrompt = async (sceneId, instruction) => {
    const { result } = await fetchRequest({
      method: "PATCH",
      path: `sessions/storyboards/${storyboardId}/scenes/${sceneId}/revise`,
      data: JSON.stringify({ revisionInstructions: instruction }),
    });
    return result; 
  };

  const apiEditPrompt = async (sceneId, payload) => {
    const { result } = await fetchRequest({
      method: "PATCH",
      path: `sessions/storyboards/${storyboardId}/scenes/${sceneId}/edit`,
      data: JSON.stringify(payload), 
    });
    return result;
  };

  const openEdit = (scene) => (e) => {
    stop(e);
    const pid = promptIdOf(scene);
    if (!pid) return;

    setEditedById((prev) => ({
      ...prev,
      [pid]: {
        name: scene?.name ?? "",
        description: scene?.description ?? "",
      },
    }));
    setMode(pid, "edit");
  };

  const openRevise = (scene) => (e) => {
    stop(e);
    const pid = promptIdOf(scene);
    if (!pid) return;

    setMode(pid, "revise");
    setReviseTextById((prev) => ({ ...prev, [pid]: prev?.[pid] ?? "" }));
  };

  const onEditChange = (pid, field) => (e) => {
    const value = e.target.value;
    setEditedById((prev) => ({
      ...prev,
      [pid]: { ...prev[pid], [field]: value },
    }));
  };

  const submitEdit = (scene) => async (e) => {
    stop(e);
    const pid = promptIdOf(scene);
    if (!pid) return;

    const payload = {
        name: (editedById?.[pid]?.name ?? "").trim(),
        description: (editedById?.[pid]?.description ?? "").trim(),
    };
    if (!payload.name && !payload.description) return;

    try {
      setEditingKey(pid);
      const updated = await apiEditPrompt(pid, payload);

      setScenes?.((prev) => {
        const list = prev ?? [];
        const next = new Array(list.length);

        for (let i = 0; i < list.length; i++) {
          const s = list[i];
          const spid = String(promptIdOf(s));

          if (spid !== String(pid)) {
            next[i] = s;
            continue;
          }

          next[i] = {
            ...s,
            name: updated?.name ?? payload.name ?? s.name,
            description: updated?.description ?? payload.description ?? s.description,
          };
        }

        return next;
      });

      doneMode(pid);
    } finally {
      setEditingKey(null);
    }
  };

  const submitRevise = (scene) => async (e) => {
    stop(e);
    const pid = promptIdOf(scene);
    if (!pid) return;

    const instruction = (reviseTextById?.[pid] ?? "").trim();
    if (!instruction) return;

    try {
      setRevisingKey(pid);
      const updated = await apiRevisePrompt(pid, instruction);

      setScenes?.((prev) => {
        const list = prev ?? [];
        const next = new Array(list.length);

        for (let i = 0; i < list.length; i++) {
          const s = list[i];
          const spid = String(promptIdOf(s));

          if (spid !== String(pid)) {
            next[i] = s;
            continue;
          }

          next[i] = {
            ...s,
            name: updated?.name ?? s.name,
            description: updated?.description ?? s.description,
          };
        }

        return next;
      });

      doneMode(pid);
    } finally {
      setRevisingKey(null);
    }
  };

  const displayScenes = useMemo(() => scenes ?? [], [scenes]);

  return (
    <div className="storyboard-container">
      {error && (
        <Alert
          type="error"
          showIcon
          message="Couldn’t fetch storyboard"
          description={error}
          style={{ margin: 20 }}
        />
      )}

      {loading ? (
        <div className="loading-container">
          {loadingDesc}
          <Spin />
        </div>
      ) : (
        <Row className="prompt-choices-container-s" gutter={[0, 10]}>
          {displayScenes.map((scene, idx) => {
            const pid = promptIdOf(scene);
            const mode = pid ? modes[pid] : null;

            const isRevising = pid && revisingKey === pid;
            const isSavingEdit = pid && editingKey === pid;
            const spinning = isRevising || isSavingEdit;

            const titleToShow = mode === "edit" ? editedById?.[pid]?.name ?? scene.name : scene.name;

            const descToShow =
              mode === "edit" ? editedById?.[pid]?.description ?? scene.description : scene.description;

            return (
              <Col key={scene.id ?? pid ?? idx} span={24}>
                <Card className="pick-card">
                  <Spin spinning={spinning}>
                    <div className="row-title">
                      <h4>Scene {idx + 1}</h4>

                      {mode !== "edit" && <div className="pick-title-s">{titleToShow}</div>}

                      <div onClick={stop}>
                        {!mode ? (
                          <>
                            <Button
                                type="link"
                                onClick={openRevise(scene)}
                                disabled={!pid || revisingKey === pid || editingKey === pid}
                                >
                                Revise
                            </Button>

                            <Button
                                type="link"
                                onClick={openEdit(scene)}
                                disabled={!pid || revisingKey === pid || editingKey === pid}
                                >
                                Edit
                            </Button>
                          </>
                        ) : (
                          <Button
                            type="link"
                            onClick={(e) => { stop(e); doneMode(pid); }}
                            disabled={revisingKey === pid || editingKey === pid}
                          >
                            Close
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="pick-desc">
                      {mode === "edit" ? (
                        <div onClick={stop}>
                          <div>
                            <label>Title</label>
                            <TextArea
                              value={editedById?.[pid]?.name ?? ""}
                              onChange={onEditChange(pid, "name")}
                              autoSize={{ minRows: 1, maxRows: 2 }}
                              disabled={spinning}
                            />
                          </div>

                          <div style={{ marginTop: 10 }}>
                            <label>Description</label>
                            <TextArea
                              value={editedById?.[pid]?.description ?? ""}
                              onChange={onEditChange(pid, "description")}
                              autoSize={{ minRows: 3, maxRows: 6 }}
                              disabled={spinning}
                            />
                          </div>

                          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                            <Button type="primary" onClick={submitEdit(scene)} loading={isSavingEdit}>
                              Save
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p>{descToShow}</p>
                      )}

                      {mode === "revise" && (
                        <div className="prompt-container" onClick={stop}>
                          <Input
                            placeholder="Revision instructions"
                            value={reviseTextById?.[pid] ?? ""}
                            onChange={(e) =>
                              setReviseTextById((prev) => ({ ...prev, [pid]: e.target.value }))
                            }
                            disabled={isRevising}
                          />
                          <Button onClick={submitRevise(scene)} loading={isRevising} disabled={isRevising}>
                            Revise Scene Prompt
                          </Button>
                        </div>
                      )}
                    </div>
                  </Spin>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
};

export default ReviewStoryboard;